import React from 'react';
import { Button, Result, Space, Typography } from 'antd';

const { Text } = Typography;

// Short, admin-quotable ID — easy to read aloud on Slack / in a ticket.
// Non-crypto, just a locator.
const newErrorId = () => {
    const rand = Math.random().toString(36).slice(2, 8);
    const time = Date.now().toString(36).slice(-4);
    return `err-${time}${rand}`;
};

// Module-level hook so one call site (src/index.js) can wire a telemetry provider for every ErrorBoundary.
let telemetryHandler = null;
export const setErrorTelemetry = (handler) => {
    telemetryHandler = typeof handler === 'function' ? handler : null;
};

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, errorId: null };
    }

    static getDerivedStateFromError(error) {
        return { error, errorId: newErrorId() };
    }

    componentDidCatch(error, errorInfo) {
        const { errorId } = this.state;
        // eslint-disable-next-line no-console
        console.error(`[ErrorBoundary ${errorId}]`, error, errorInfo);
        if (telemetryHandler) {
            try {
                telemetryHandler({ error, errorInfo, errorId });
            } catch (telemetryError) {
                // eslint-disable-next-line no-console
                console.error('[ErrorBoundary] telemetry handler threw', telemetryError);
            }
        }
    }

    reset = () => {
        this.setState({ error: null, errorId: null });
    };

    render() {
        if (this.state.error) {
            const { fallback, title = 'Something went wrong' } = this.props;
            const { errorId } = this.state;
            if (typeof fallback === 'function') {
                return fallback({ error: this.state.error, errorId, reset: this.reset });
            }
            return (
                <div role='alert' aria-live='assertive'>
                    <Result
                        status='error'
                        title={title}
                        subTitle={
                            <Space direction='vertical' size={4} align='center'>
                                <Text>
                                    {this.state.error?.message ||
                                        'An unexpected error occurred. Try again, or contact support if the issue persists.'}
                                </Text>
                                <Text type='secondary' style={{ fontSize: 12 }}>
                                    Share this ID with engineering:{' '}
                                    <Text code copyable={{ text: errorId }}>
                                        {errorId}
                                    </Text>
                                </Text>
                            </Space>
                        }
                        extra={
                            <Button type='primary' onClick={this.reset}>
                                Try Again
                            </Button>
                        }
                    />
                </div>
            );
        }
        return this.props.children;
    }
}
